import React, { useMemo, useState } from 'react';
import { BarChart3, TrendingUp, ArrowUpRight, ArrowDownRight, Clock, Target, AlertCircle, DollarSign, Users } from 'lucide-react';
import { ResponsiveContainer, ComposedChart, Area, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';
const MONTHLY_DATA = [{
  month: 'Jan',
  fullyBooked: 420000,
  previousYear: 280000,
  pipeline: 620000,
  quotes: 42
}, {
  month: 'Feb',
  fullyBooked: 580000,
  previousYear: 340000,
  pipeline: 740000,
  quotes: 51
}, {
  month: 'Mar',
  fullyBooked: 510000,
  previousYear: 380000,
  pipeline: 680000,
  quotes: 47
}, {
  month: 'Apr',
  fullyBooked: 690000,
  previousYear: 420000,
  pipeline: 880000,
  quotes: 58
}, {
  month: 'May',
  fullyBooked: 820000,
  previousYear: 460000,
  pipeline: 1020000,
  quotes: 64
}, {
  month: 'Jun',
  fullyBooked: 740000,
  previousYear: 490000,
  pipeline: 940000,
  quotes: 60
}, {
  month: 'Jul',
  fullyBooked: 910000,
  previousYear: 520000,
  pipeline: 1160000,
  quotes: 72
}, {
  month: 'Aug',
  fullyBooked: 1050000,
  previousYear: 580000,
  pipeline: 1280000,
  quotes: 81
}, {
  month: 'Sep',
  fullyBooked: 980000,
  previousYear: 610000,
  pipeline: 1210000,
  quotes: 77
}, {
  month: 'Oct',
  fullyBooked: 1180000,
  previousYear: 640000,
  pipeline: 1420000,
  quotes: 89
}, {
  month: 'Nov',
  fullyBooked: 1320000,
  previousYear: 680000,
  pipeline: 1580000,
  quotes: 96
}, {
  month: 'Dec',
  fullyBooked: 1450000,
  previousYear: 720000,
  pipeline: 1720000,
  quotes: 104
}];
const QUARTERLY_DATA = [{
  month: 'Q1',
  fullyBooked: 1510000,
  previousYear: 1000000,
  pipeline: 2040000,
  quotes: 140
}, {
  month: 'Q2',
  fullyBooked: 2250000,
  previousYear: 1370000,
  pipeline: 2840000,
  quotes: 182
}, {
  month: 'Q3',
  fullyBooked: 2940000,
  previousYear: 1710000,
  pipeline: 3650000,
  quotes: 230
}, {
  month: 'Q4',
  fullyBooked: 3950000,
  previousYear: 2040000,
  pipeline: 4720000,
  quotes: 289
}];
const WEEKLY_DATA = Array.from({
  length: 13
}, (_, i) => {
  const base = 280000 + i * 18000 + Math.sin(i * 0.9) * 40000;
  return {
    month: `W${i + 1}`,
    fullyBooked: Math.round(base),
    previousYear: Math.round(base * 0.62),
    pipeline: Math.round(base * 1.28),
    quotes: Math.round(20 + i * 1.5)
  };
});
const formatCurrency = (val: number) => {
  if (val >= 1000000) return `${(val / 1000000).toFixed(1)}M`;
  if (val >= 1000) return `${Math.round(val / 1000)}K`;
  return `${val}`;
};
const CustomTooltip = ({
  active,
  payload,
  label
}: any) => {
  if (!active || !payload || !payload.length) return null;
  const data = payload[0].payload;
  const delta = data.fullyBooked - data.previousYear;
  const deltaPct = data.previousYear > 0 ? delta / data.previousYear * 100 : 0;
  return <div className="bg-stone-900 text-stone-50 border border-stone-800 shadow-xl p-4 min-w-[220px]" data-id="element-318">
      <p className="font-mono text-xs uppercase tracking-widest text-stone-400 mb-3 pb-2 border-b border-stone-800" data-id="element-319">
        {label}
      </p>
      <div className="space-y-2.5" data-id="element-320">
        <div className="flex items-center justify-between gap-6" data-id="element-321">
          <div className="flex items-center gap-2" data-id="element-322">
            <div className="w-2.5 h-2.5 bg-red-600 rounded-sm" data-id="element-323" />
            <span className="text-xs text-stone-300" data-id="element-324">Fully Booked</span>
          </div>
          <span className="font-mono text-sm text-stone-50" data-id="element-325">
            {formatCurrency(data.fullyBooked)}
          </span>
        </div>
        <div className="flex items-center justify-between gap-6" data-id="element-326">
          <div className="flex items-center gap-2" data-id="element-327">
            <div className="w-2.5 h-2.5 bg-red-300 rounded-sm" data-id="element-328" />
            <span className="text-xs text-stone-300" data-id="element-329">Pipeline</span>
          </div>
          <span className="font-mono text-sm text-stone-50" data-id="element-330">
            {formatCurrency(data.pipeline)}
          </span>
        </div>
        <div className="flex items-center justify-between gap-6" data-id="element-331">
          <div className="flex items-center gap-2" data-id="element-332">
            <div className="w-2.5 h-2.5 bg-stone-500 rounded-sm" data-id="element-333" />
            <span className="text-xs text-stone-300" data-id="element-334">Previous Year</span>
          </div>
          <span className="font-mono text-sm text-stone-400" data-id="element-335">
            {formatCurrency(data.previousYear)}
          </span>
        </div>
      </div>
      <div className="mt-3 pt-3 border-t border-stone-800 flex items-center justify-between" data-id="element-336">
        <span className="text-xs text-stone-400" data-id="element-337">YoY Growth</span>
        <span className={`font-mono text-xs flex items-center gap-1 ${deltaPct >= 0 ? 'text-emerald-400' : 'text-red-400'}`} data-id="element-338">
          {deltaPct >= 0 ? <ArrowUpRight className="w-3 h-3" data-id="element-339" /> : <ArrowDownRight className="w-3 h-3" data-id="element-340" />}
          {Math.abs(deltaPct).toFixed(1)}%
        </span>
      </div>
      <div className="mt-2 flex items-center justify-between" data-id="element-341">
        <span className="text-xs text-stone-400" data-id="element-342">Quotes</span>
        <span className="font-mono text-xs text-stone-300" data-id="element-343">{data.quotes}</span>
      </div>
    </div>;
};
type RangeKey = '13W' | '12M' | '4Q';
export function AnalyticsTab() {
  const [range, setRange] = useState<RangeKey>('12M');
  const chartData = useMemo(() => {
    if (range === '13W') return WEEKLY_DATA;
    if (range === '4Q') return QUARTERLY_DATA;
    return MONTHLY_DATA;
  }, [range]);
  const totals = useMemo(() => {
    const fullyBooked = chartData.reduce((s, d) => s + d.fullyBooked, 0);
    const previousYear = chartData.reduce((s, d) => s + d.previousYear, 0);
    const pipeline = chartData.reduce((s, d) => s + d.pipeline, 0);
    const yoy = previousYear > 0 ? (fullyBooked - previousYear) / previousYear * 100 : 0;
    return {
      fullyBooked,
      previousYear,
      pipeline,
      yoy
    };
  }, [chartData]);
  return <div className="space-y-8 animate-in fade-in duration-500 pb-24" data-id="element-344">
      <div data-id="element-345">
        <h1 className="font-serif text-3xl text-stone-900 mb-2" data-id="element-346">
          Analytics & Intelligence
        </h1>
        <p className="text-stone-500" data-id="element-347">
          Track your pipeline from RFP to fully booked revenue.
        </p>
      </div>

      {/* Main Chart Area */}
      <div className="bg-white border border-stone-200 shadow-sm p-6" data-id="element-348">
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 mb-6" data-id="element-349">
          <div data-id="element-350">
            <h2 className="font-serif text-xl text-stone-900" data-id="element-351">
              Fully Booked Revenue
            </h2>
            <p className="text-sm text-stone-500" data-id="element-352">
              Pipeline → confirmed bookings, with prior-year comparison
            </p>
          </div>
          <div className="flex items-center bg-stone-100 p-1 rounded-md" data-id="element-353">
            {(['13W', '12M', '4Q'] as RangeKey[]).map(k => <button key={k} onClick={() => setRange(k)} className={`px-3 py-1 text-xs font-mono uppercase tracking-widest rounded transition-colors ${range === k ? 'bg-white text-stone-900 shadow-sm' : 'text-stone-500 hover:text-stone-900'}`} data-id="element-354">
                {k}
              </button>)}
          </div>
        </div>

        {/* Top stat row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-8 pb-6 border-b border-stone-100" data-id="element-355">
          <div data-id="element-356">
            <p className="font-mono text-xs uppercase tracking-widest text-stone-500 mb-2" data-id="element-357">
              Fully Booked
            </p>
            <p className="font-mono text-2xl text-stone-900" data-id="element-358">
              {formatCurrency(totals.fullyBooked)}
            </p>
            <p className="text-xs text-emerald-600 mt-1 flex items-center gap-1" data-id="element-359">
              <ArrowUpRight className="w-3 h-3" data-id="element-360" />
              {totals.yoy.toFixed(1)}% YoY
            </p>
          </div>
          <div data-id="element-361">
            <p className="font-mono text-xs uppercase tracking-widest text-stone-500 mb-2" data-id="element-362">
              Pipeline
            </p>
            <p className="font-mono text-2xl text-stone-900" data-id="element-363">
              {formatCurrency(totals.pipeline)}
            </p>
            <p className="text-xs text-stone-500 mt-1" data-id="element-364">Active quotes + holds</p>
          </div>
          <div data-id="element-365">
            <p className="font-mono text-xs uppercase tracking-widest text-stone-500 mb-2" data-id="element-366">
              Conversion
            </p>
            <p className="font-mono text-2xl text-stone-900" data-id="element-367">
              {(totals.fullyBooked / totals.pipeline * 100).toFixed(0)}%
            </p>
            <p className="text-xs text-stone-500 mt-1" data-id="element-368">Pipeline → booked</p>
          </div>
          <div data-id="element-369">
            <p className="font-mono text-xs uppercase tracking-widest text-stone-500 mb-2" data-id="element-370">
              Previous Year
            </p>
            <p className="font-mono text-2xl text-stone-400" data-id="element-371">
              {formatCurrency(totals.previousYear)}
            </p>
            <p className="text-xs text-stone-500 mt-1" data-id="element-372">Same period</p>
          </div>
        </div>

        {/* Legend */}
        <div className="flex flex-wrap items-center gap-5 mb-4" data-id="element-373">
          <div className="flex items-center gap-2" data-id="element-374">
            <div className="w-3 h-3 bg-red-600 rounded-sm" data-id="element-375" />
            <span className="text-xs text-stone-600 font-mono uppercase tracking-widest" data-id="element-376">
              Fully Booked
            </span>
          </div>
          <div className="flex items-center gap-2" data-id="element-377">
            <div className="w-3 h-1 bg-red-300 rounded-sm" data-id="element-378" />
            <span className="text-xs text-stone-600 font-mono uppercase tracking-widest" data-id="element-379">
              Pipeline
            </span>
          </div>
          <div className="flex items-center gap-2" data-id="element-380">
            <div className="w-3 h-0.5 bg-stone-400" style={{
            backgroundImage: 'repeating-linear-gradient(90deg, #a8a29e 0, #a8a29e 2px, transparent 2px, transparent 4px)'
          }} data-id="element-381" />
            <span className="text-xs text-stone-600 font-mono uppercase tracking-widest" data-id="element-382">
              Previous Year
            </span>
          </div>
        </div>

        {/* Recharts ComposedChart */}
        <div className="h-80 w-full" data-id="element-383">
          <ResponsiveContainer width="100%" height="100%" data-id="element-384">
            <ComposedChart data={chartData} margin={{
            top: 16,
            right: 16,
            left: 0,
            bottom: 8
          }} data-id="element-385">
              <defs data-id="element-386">
                <linearGradient id="fullyBookedFill" x1="0" y1="0" x2="0" y2="1" data-id="element-387">
                  <stop offset="0%" stopColor="#DC2626" stopOpacity={0.35} data-id="element-388" />
                  <stop offset="100%" stopColor="#DC2626" stopOpacity={0.02} data-id="element-389" />
                </linearGradient>
                <linearGradient id="pipelineFill" x1="0" y1="0" x2="0" y2="1" data-id="element-390">
                  <stop offset="0%" stopColor="#FCA5A5" stopOpacity={0.25} data-id="element-391" />
                  <stop offset="100%" stopColor="#FCA5A5" stopOpacity={0} data-id="element-392" />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="#E7E5E4" strokeDasharray="2 4" vertical={false} data-id="element-393" />
              <XAxis dataKey="month" tick={{
              fill: '#A8A29E',
              fontFamily: 'IBM Plex Mono',
              fontSize: 11
            }} tickLine={false} axisLine={{
              stroke: '#E7E5E4'
            }} data-id="element-394" />
              <YAxis tick={{
              fill: '#A8A29E',
              fontFamily: 'IBM Plex Mono',
              fontSize: 11
            }} tickLine={false} axisLine={false} tickFormatter={v => formatCurrency(v)} width={56} data-id="element-395" />
              <Tooltip content={<CustomTooltip data-id="element-397" />} cursor={{
              stroke: '#DC2626',
              strokeWidth: 1,
              strokeDasharray: '4 4'
            }} data-id="element-396" />
              <Area type="monotone" dataKey="pipeline" stroke="#FCA5A5" strokeWidth={2} strokeDasharray="6 3" fill="url(#pipelineFill)" dot={false} activeDot={{
              r: 5,
              fill: '#FCA5A5',
              stroke: '#fff',
              strokeWidth: 2
            }} animationDuration={800} data-id="element-398" />
              <Area type="monotone" dataKey="fullyBooked" stroke="#DC2626" strokeWidth={2.5} fill="url(#fullyBookedFill)" dot={false} activeDot={{
              r: 6,
              fill: '#DC2626',
              stroke: '#fff',
              strokeWidth: 2
            }} animationDuration={800} data-id="element-399" />
              <Line type="monotone" dataKey="previousYear" stroke="#A8A29E" strokeWidth={1.5} strokeDasharray="3 3" dot={false} activeDot={{
              r: 4,
              fill: '#A8A29E',
              stroke: '#fff',
              strokeWidth: 2
            }} animationDuration={800} data-id="element-400" />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Highlight: Time to Money */}
      <div className="bg-stone-900 text-stone-50 p-8 border border-stone-800 shadow-lg relative overflow-hidden" data-id="element-401">
        <div className="absolute top-0 right-0 w-64 h-64 bg-red-600/10 rounded-full blur-3xl -mr-20 -mt-20 pointer-events-none" data-id="element-402" />
        <div className="relative z-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-8" data-id="element-403">
          <div data-id="element-404">
            <div className="flex items-center gap-3 mb-2" data-id="element-405">
              <Clock className="w-5 h-5 text-red-500" data-id="element-406" />
              <h2 className="font-serif text-2xl" data-id="element-407">Time to Money</h2>
            </div>
            <p className="text-stone-400 max-w-xl" data-id="element-408">
              The booking is not truly real until contract and deposit are
              confirmed. BlockQuote tracks the full path from request to fully
              booked revenue.
            </p>
          </div>
          <div className="flex items-center gap-8" data-id="element-409">
            <div data-id="element-410">
              <p className="font-mono text-xs uppercase tracking-widest text-stone-500 mb-1" data-id="element-411">
                Avg Time to Quote
              </p>
              <p className="font-mono text-3xl text-stone-50" data-id="element-412">
                4.2<span className="text-sm text-stone-400 ml-1" data-id="element-413">mins</span>
              </p>
            </div>
            <div className="w-px h-12 bg-stone-800 hidden md:block" data-id="element-414" />
            <div data-id="element-415">
              <p className="font-mono text-xs uppercase tracking-widest text-stone-500 mb-1" data-id="element-416">
                Avg Time to Fully Booked
              </p>
              <p className="font-mono text-3xl text-emerald-400" data-id="element-417">
                14<span className="text-sm text-stone-400 ml-1" data-id="element-418">days</span>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Pipeline Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-419">
        <div className="bg-white p-6 border border-stone-200 shadow-sm" data-id="element-420">
          <p className="font-mono text-xs uppercase tracking-widest text-stone-500 mb-4" data-id="element-421">
            Total Quote Requests
          </p>
          <p className="font-mono text-3xl text-stone-900" data-id="element-422">842</p>
          <p className="text-sm text-stone-500 mt-2 flex items-center gap-1" data-id="element-423">
            <ArrowUpRight className="w-4 h-4 text-emerald-600" data-id="element-424" />
            <span className="text-emerald-600 font-medium" data-id="element-425">18%</span> vs last
            month
          </p>
        </div>
        <div className="bg-white p-6 border border-stone-200 shadow-sm" data-id="element-426">
          <p className="font-mono text-xs uppercase tracking-widest text-stone-500 mb-4" data-id="element-427">
            Quote Approval Rate
          </p>
          <p className="font-mono text-3xl text-stone-900" data-id="element-428">92%</p>
          <p className="text-sm text-stone-500 mt-2" data-id="element-429">Approved by resort team</p>
        </div>
        <div className="bg-white p-6 border border-stone-200 shadow-sm" data-id="element-430">
          <p className="font-mono text-xs uppercase tracking-widest text-stone-500 mb-4" data-id="element-431">
            Agency View Rate
          </p>
          <p className="font-mono text-3xl text-stone-900" data-id="element-432">88%</p>
          <p className="text-sm text-stone-500 mt-2" data-id="element-433">Opened by agency</p>
        </div>
        <div className="bg-white p-6 border border-stone-200 shadow-sm" data-id="element-434">
          <p className="font-mono text-xs uppercase tracking-widest text-stone-500 mb-4" data-id="element-435">
            Hold Rate
          </p>
          <p className="font-mono text-3xl text-stone-900" data-id="element-436">45%</p>
          <p className="text-sm text-stone-500 mt-2" data-id="element-437">Moved to hold status</p>
        </div>
        <div className="bg-white p-6 border border-stone-200 shadow-sm" data-id="element-438">
          <p className="font-mono text-xs uppercase tracking-widest text-stone-500 mb-4" data-id="element-439">
            Contract Rate
          </p>
          <p className="font-mono text-3xl text-stone-900" data-id="element-440">38%</p>
          <p className="text-sm text-stone-500 mt-2" data-id="element-441">Contract issued</p>
        </div>
        <div className="bg-white p-6 border border-stone-200 shadow-sm" data-id="element-442">
          <p className="font-mono text-xs uppercase tracking-widest text-stone-500 mb-4" data-id="element-443">
            Fully Booked Value
          </p>
          <p className="font-mono text-3xl text-emerald-600" data-id="element-444">$4.25M</p>
          <p className="text-sm text-stone-500 mt-2" data-id="element-445">Deposit confirmed</p>
        </div>
        <div className="bg-white p-6 border border-stone-200 shadow-sm" data-id="element-446">
          <p className="font-mono text-xs uppercase tracking-widest text-stone-500 mb-4" data-id="element-447">
            Lost Pipeline Value
          </p>
          <p className="font-mono text-3xl text-stone-900" data-id="element-448">$1.8M</p>
          <p className="text-sm text-stone-500 mt-2" data-id="element-449">Rejected or expired</p>
        </div>
        <div className="bg-white p-6 border border-stone-200 shadow-sm" data-id="element-450">
          <p className="font-mono text-xs uppercase tracking-widest text-stone-500 mb-4" data-id="element-451">
            Leakage Prevented
          </p>
          <p className="font-mono text-3xl text-stone-900" data-id="element-452">$382.5K</p>
          <p className="text-sm text-stone-500 mt-2" data-id="element-453">Commission saved YTD</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-454">
        {/* Top Agencies */}
        <div className="bg-white border border-stone-200 shadow-sm p-6" data-id="element-455">
          <div className="flex items-center gap-3 mb-6" data-id="element-456">
            <div className="p-2 bg-stone-100 text-stone-700 rounded-md" data-id="element-457">
              <Users className="w-5 h-5" data-id="element-458" />
            </div>
            <h2 className="font-serif text-xl text-stone-900" data-id="element-459">Top Agencies</h2>
          </div>

          <div className="space-y-6" data-id="element-460">
            <div data-id="element-461">
              <p className="font-mono text-xs uppercase tracking-widest text-stone-500 mb-3" data-id="element-462">
                By Fully Booked Value
              </p>
              <div className="space-y-3" data-id="element-463">
                <div className="flex justify-between text-sm" data-id="element-464">
                  <span className="text-stone-900 font-medium" data-id="element-465">
                    Liberty Travel
                  </span>
                  <span className="font-mono text-emerald-600" data-id="element-466">$845,000</span>
                </div>
                <div className="flex justify-between text-sm" data-id="element-467">
                  <span className="text-stone-900 font-medium" data-id="element-468">
                    Flight Centre
                  </span>
                  <span className="font-mono text-emerald-600" data-id="element-469">$620,000</span>
                </div>
                <div className="flex justify-between text-sm" data-id="element-470">
                  <span className="text-stone-900 font-medium" data-id="element-471">
                    Travel Leaders
                  </span>
                  <span className="font-mono text-emerald-600" data-id="element-472">$480,000</span>
                </div>
              </div>
            </div>

            <div className="pt-4 border-t border-stone-100" data-id="element-473">
              <p className="font-mono text-xs uppercase tracking-widest text-stone-500 mb-3" data-id="element-474">
                By Conversion Rate
              </p>
              <div className="space-y-3" data-id="element-475">
                <div className="flex justify-between text-sm" data-id="element-476">
                  <span className="text-stone-900 font-medium" data-id="element-477">DestaWed</span>
                  <span className="font-mono text-stone-900" data-id="element-478">68%</span>
                </div>
                <div className="flex justify-between text-sm" data-id="element-479">
                  <span className="text-stone-900 font-medium" data-id="element-480">ProTravel</span>
                  <span className="font-mono text-stone-900" data-id="element-481">54%</span>
                </div>
                <div className="flex justify-between text-sm" data-id="element-482">
                  <span className="text-stone-900 font-medium" data-id="element-483">Direct</span>
                  <span className="font-mono text-stone-900" data-id="element-484">48%</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Lost Deal Reasons */}
        <div className="bg-white border border-stone-200 shadow-sm p-6" data-id="element-485">
          <div className="flex items-center gap-3 mb-6" data-id="element-486">
            <div className="p-2 bg-red-100 text-red-700 rounded-md" data-id="element-487">
              <AlertCircle className="w-5 h-5" data-id="element-488" />
            </div>
            <h2 className="font-serif text-xl text-stone-900" data-id="element-489">
              Learn Why Deals Are Lost
            </h2>
          </div>

          <p className="text-sm text-stone-500 mb-6" data-id="element-490">
            When a quote is rejected, ignored, expired, or not moved to hold,
            BlockQuote captures the reason so the resort can improve future
            conversion.
          </p>

          <div className="space-y-4" data-id="element-491">
            <div data-id="element-492">
              <div className="flex justify-between text-sm mb-1" data-id="element-493">
                <span className="text-stone-900 font-medium" data-id="element-494">
                  Price too high
                </span>
                <span className="font-mono text-stone-500" data-id="element-495">42%</span>
              </div>
              <div className="w-full bg-stone-100 h-2 rounded-full overflow-hidden" data-id="element-496">
                <div className="bg-red-500 h-full rounded-full" style={{
                width: '42%'
              }} data-id="element-497" />
              </div>
            </div>
            <div data-id="element-498">
              <div className="flex justify-between text-sm mb-1" data-id="element-499">
                <span className="text-stone-900 font-medium" data-id="element-500">
                  Competitor selected
                </span>
                <span className="font-mono text-stone-500" data-id="element-501">28%</span>
              </div>
              <div className="w-full bg-stone-100 h-2 rounded-full overflow-hidden" data-id="element-502">
                <div className="bg-red-400 h-full rounded-full" style={{
                width: '28%'
              }} data-id="element-503" />
              </div>
            </div>
            <div data-id="element-504">
              <div className="flex justify-between text-sm mb-1" data-id="element-505">
                <span className="text-stone-900 font-medium" data-id="element-506">
                  No availability
                </span>
                <span className="font-mono text-stone-500" data-id="element-507">15%</span>
              </div>
              <div className="w-full bg-stone-100 h-2 rounded-full overflow-hidden" data-id="element-508">
                <div className="bg-red-300 h-full rounded-full" style={{
                width: '15%'
              }} data-id="element-509" />
              </div>
            </div>
            <div data-id="element-510">
              <div className="flex justify-between text-sm mb-1" data-id="element-511">
                <span className="text-stone-900 font-medium" data-id="element-512">
                  Destination changed
                </span>
                <span className="font-mono text-stone-500" data-id="element-513">10%</span>
              </div>
              <div className="w-full bg-stone-100 h-2 rounded-full overflow-hidden" data-id="element-514">
                <div className="bg-stone-300 h-full rounded-full" style={{
                width: '10%'
              }} data-id="element-515" />
              </div>
            </div>
            <div data-id="element-516">
              <div className="flex justify-between text-sm mb-1" data-id="element-517">
                <span className="text-stone-900 font-medium" data-id="element-518">
                  Event space issue
                </span>
                <span className="font-mono text-stone-500" data-id="element-519">5%</span>
              </div>
              <div className="w-full bg-stone-100 h-2 rounded-full overflow-hidden" data-id="element-520">
                <div className="bg-stone-200 h-full rounded-full" style={{
                width: '5%'
              }} data-id="element-521" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}